import React, { useState, useCallback, useRef, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';

// ─── Animations ─────────────────────────────────────────────
const bounce = keyframes`
  0%,100%{transform:translateY(0) scale(1)}
  30%{transform:translateY(-12px) scale(1.12)}
  60%{transform:translateY(-5px) scale(1.05)}
`;
const shake = keyframes`
  0%,100%{transform:translateX(0)}
  20%{transform:translateX(-8px)}
  40%{transform:translateX(8px)}
  60%{transform:translateX(-6px)}
  80%{transform:translateX(6px)}
`;
const popIn = keyframes`
  0%{transform:scale(0.4) rotate(-10deg);opacity:0}
  70%{transform:scale(1.1) rotate(3deg);opacity:1}
  100%{transform:scale(1) rotate(0deg);opacity:1}
`;
const starSpin = keyframes`
  0%{transform:rotate(0deg) scale(0);opacity:0}
  50%{transform:rotate(180deg) scale(1.5);opacity:1}
  100%{transform:rotate(360deg) scale(0);opacity:0}
`;
const floatUp = keyframes`
  0%{transform:translateY(0);opacity:1}
  100%{transform:translateY(-80px);opacity:0}
`;
const pulse = keyframes`
  0%,100%{box-shadow:0 0 0 0 rgba(99,102,241,0.4)}
  50%{box-shadow:0 0 0 12px rgba(99,102,241,0)}
`;
const rainbow = keyframes`
  0%{background-position:0% 50%}
  50%{background-position:100% 50%}
  100%{background-position:0% 50%}
`;

// ─── Styled Components ───────────────────────────────────────
const PageBg = styled.div`
  min-height:100vh;
  background: linear-gradient(135deg,#1a1a2e 0%,#16213e 40%,#0f3460 100%);
  padding:16px;
  font-family:'Segoe UI',Tahoma,sans-serif;
  position:relative;
  overflow:hidden;
  &::before{
    content:'';
    position:fixed;inset:0;
    background:radial-gradient(ellipse at 20% 20%,rgba(99,102,241,0.15) 0%,transparent 60%),
               radial-gradient(ellipse at 80% 80%,rgba(236,72,153,0.12) 0%,transparent 60%);
    pointer-events:none;
  }
`;

const Stars = styled.div`
  position:fixed;inset:0;pointer-events:none;
  &::before,&::after{
    content:'';position:absolute;width:2px;height:2px;border-radius:50%;
    background:#fff;
    box-shadow: ${() => Array.from({length:80},()=>`${Math.random()*100}vw ${Math.random()*100}vh 1px rgba(255,255,255,${0.2+Math.random()*0.6})`).join(',')};
  }
`;

const Title = styled.h1`
  text-align:center;
  font-size:clamp(22px,4vw,36px);
  font-weight:900;
  margin:0 0 16px;
  background:linear-gradient(90deg,#f59e0b,#ec4899,#8b5cf6,#06b6d4,#10b981);
  background-size:300% 100%;
  animation:${rainbow} 4s ease infinite;
  -webkit-background-clip:text;
  -webkit-text-fill-color:transparent;
  background-clip:text;
  letter-spacing:-0.5px;
`;

const TeacherPanel = styled.div`
  background:rgba(255,255,255,0.06);
  border:1px solid rgba(255,255,255,0.12);
  border-radius:18px;
  padding:18px;
  margin-bottom:20px;
  backdrop-filter:blur(10px);
`;

const PanelTitle = styled.div`
  color:#a5f3fc;
  font-size:13px;
  font-weight:700;
  letter-spacing:1px;
  text-transform:uppercase;
  margin-bottom:10px;
  display:flex;align-items:center;gap:8px;
`;

const Textarea = styled.textarea`
  width:100%;
  min-height:120px;
  background:rgba(0,0,0,0.3);
  border:1px solid rgba(255,255,255,0.15);
  border-radius:12px;
  color:#f1f5f9;
  font-size:14px;
  padding:12px;
  resize:vertical;
  outline:none;
  font-family:monospace;
  line-height:1.6;
  &:focus{border-color:#818cf8;}
  &::placeholder{color:#475569;}
`;

const FormatHint = styled.div`
  margin-top:8px;color:#64748b;font-size:12px;line-height:1.6;
  span{color:#94a3b8;}
`;

const BtnRow = styled.div`
  display:flex;gap:10px;flex-wrap:wrap;margin-top:12px;
`;

const Btn = styled.button<{variant?:'primary'|'success'|'danger'|'secondary'|'warning'|'info';pulsing?:boolean}>`
  padding:9px 20px;border:none;border-radius:10px;font-weight:700;font-size:14px;
  cursor:pointer;transition:all 0.2s;display:flex;align-items:center;gap:6px;
  ${p=>p.variant==='primary'&&css`background:linear-gradient(135deg,#6366f1,#8b5cf6);color:#fff;&:hover{filter:brightness(1.15);transform:translateY(-1px);}`}
  ${p=>p.variant==='success'&&css`background:linear-gradient(135deg,#10b981,#059669);color:#fff;&:hover{filter:brightness(1.15);transform:translateY(-1px);}`}
  ${p=>p.variant==='danger'&&css`background:linear-gradient(135deg,#ef4444,#dc2626);color:#fff;&:hover{filter:brightness(1.15);}`}
  ${p=>p.variant==='warning'&&css`background:linear-gradient(135deg,#f59e0b,#d97706);color:#fff;&:hover{filter:brightness(1.15);}`}
  ${p=>p.variant==='info'&&css`background:linear-gradient(135deg,#06b6d4,#0891b2);color:#fff;&:hover{filter:brightness(1.15);}`}
  ${p=>p.variant==='secondary'&&css`background:rgba(255,255,255,0.1);color:#cbd5e1;border:1px solid rgba(255,255,255,0.15);&:hover{background:rgba(255,255,255,0.18);}`}
  ${p=>p.pulsing&&css`animation:${pulse} 1.5s infinite;`}
  &:active{transform:scale(0.96);}
`;

const QuestionBoard = styled.div`
  background:rgba(255,255,255,0.05);
  border:1px solid rgba(255,255,255,0.1);
  border-radius:24px;
  padding:24px;
  backdrop-filter:blur(10px);
  position:relative;
`;

const QHeader = styled.div`
  display:flex;align-items:center;justify-content:space-between;
  margin-bottom:20px;flex-wrap:wrap;gap:12px;
`;

const QBadge = styled.div`
  background:linear-gradient(135deg,#6366f1,#ec4899);
  color:#fff;font-weight:900;font-size:16px;
  padding:8px 20px;border-radius:20px;
  box-shadow:0 4px 15px rgba(99,102,241,0.4);
`;

const ProgressBar = styled.div<{pct:number}>`
  flex:1;max-width:300px;
  height:10px;background:rgba(255,255,255,0.1);border-radius:99px;overflow:hidden;
  &::after{
    content:'';display:block;height:100%;border-radius:99px;
    background:linear-gradient(90deg,#6366f1,#ec4899);
    width:${p=>p.pct}%;transition:width 0.5s ease;
  }
`;

const ScoreBadge = styled.div`
  color:#fbbf24;font-weight:900;font-size:18px;
  display:flex;align-items:center;gap:5px;
`;

const SectionLabel = styled.div`
  color:#94a3b8;font-size:12px;font-weight:700;
  text-transform:uppercase;letter-spacing:1px;
  margin-bottom:10px;display:flex;align-items:center;gap:6px;
`;

const WordPool = styled.div`
  display:flex;flex-wrap:wrap;gap:10px;
  min-height:60px;
  padding:14px;
  background:rgba(0,0,0,0.2);
  border-radius:16px;
  border:2px dashed rgba(255,255,255,0.1);
  margin-bottom:16px;
  align-content:flex-start;
`;

const AnswerArea = styled.div<{isCorrect?:boolean|null;isWrong?:boolean}>`
  display:flex;flex-wrap:wrap;gap:10px;
  min-height:70px;
  padding:14px;
  background:rgba(0,0,0,0.25);
  border-radius:16px;
  border:2px solid ${p=>p.isCorrect?'#10b981':p.isWrong?'#ef4444':'rgba(255,255,255,0.15)'};
  margin-bottom:16px;
  align-content:flex-start;
  transition:border-color 0.3s;
  position:relative;
  box-shadow:${p=>p.isCorrect?'0 0 20px rgba(16,185,129,0.3)':p.isWrong?'0 0 20px rgba(239,68,68,0.3)':'none'};
`;

const AnswerPlaceholder = styled.div`
  position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);
  color:#334155;font-size:14px;font-style:italic;pointer-events:none;
`;

const WordBox = styled.button<{
  color:string;
  state:'pool'|'answer';
  animState:'idle'|'bouncing'|'shaking';
}>`
  padding:20px 36px;
  border:none;
  border-radius:20px;
  font-size:30px;
  font-weight:700;
  cursor:pointer;
  color:#fff;
  background:${p=>p.color};
  box-shadow:0 6px 18px rgba(0,0,0,0.35), inset 0 2px 0 rgba(255,255,255,0.2);
  transition:transform 0.15s,filter 0.15s,box-shadow 0.15s;
  position:relative;
  text-shadow:0 2px 4px rgba(0,0,0,0.3);
  ${p=>p.animState==='bouncing'&&css`animation:${bounce} 0.5s ease;`}
  ${p=>p.animState==='shaking'&&css`animation:${shake} 0.45s ease;`}
  &:hover{filter:brightness(1.2);transform:scale(1.05) translateY(-3px);box-shadow:0 12px 28px rgba(0,0,0,0.45);}
  &:active{transform:scale(0.96);filter:brightness(0.95);}
  ${p=>p.state==='pool'&&css`animation-fill-mode:both;`}
`;

const ResultBanner = styled.div<{ok:boolean}>`
  text-align:center;padding:16px;border-radius:16px;
  font-size:20px;font-weight:900;
  background:${p=>p.ok?'linear-gradient(135deg,rgba(16,185,129,0.25),rgba(5,150,105,0.15))':'linear-gradient(135deg,rgba(239,68,68,0.25),rgba(220,38,38,0.15))'};
  border:2px solid ${p=>p.ok?'#10b981':'#ef4444'};
  color:${p=>p.ok?'#6ee7b7':'#fca5a5'};
  margin-bottom:16px;
  position:relative;overflow:hidden;
`;

const CorrectAnswerHint = styled.div`
  background:rgba(99,102,241,0.15);
  border:1px solid rgba(99,102,241,0.3);
  border-radius:12px;
  padding:16px 20px;
  color:#a5b4fc;
  font-size:44px;
  font-weight:600;
  margin-bottom:16px;
  line-height:1.5;
  span{font-weight:800;color:#c7d2fe;}
`;

const NavRow = styled.div`
  display:flex;justify-content:center;gap:12px;flex-wrap:wrap;
`;

const FloatingEmoji = styled.div<{x:number;animKey:number}>`
  position:absolute;
  left:${p=>p.x}%;
  top:50%;
  font-size:28px;
  pointer-events:none;
  animation:${floatUp} 1s ease forwards;
  z-index:10;
`;

const NoExercises = styled.div`
  text-align:center;padding:60px 20px;color:#475569;
  font-size:16px;
`;

const EmptyState = () => (
  <NoExercises>
    <div style={{fontSize:60,marginBottom:16}}>📝</div>
    <div style={{fontSize:18,color:'#64748b',fontWeight:700,marginBottom:8}}>Chưa có bài tập</div>
    <div style={{fontSize:14,color:'#475569'}}>Paste bài tập vào ô bên trên rồi nhấn <strong style={{color:'#818cf8'}}>Tải bài tập</strong></div>
  </NoExercises>
);

// ─── Colour palette for word boxes ───────────────────────────
const WORD_COLORS = [
  'linear-gradient(135deg,#6366f1,#4f46e5)',
  'linear-gradient(135deg,#ec4899,#db2777)',
  'linear-gradient(135deg,#f59e0b,#d97706)',
  'linear-gradient(135deg,#10b981,#059669)',
  'linear-gradient(135deg,#06b6d4,#0891b2)',
  'linear-gradient(135deg,#8b5cf6,#7c3aed)',
  'linear-gradient(135deg,#ef4444,#dc2626)',
  'linear-gradient(135deg,#3b82f6,#2563eb)',
  'linear-gradient(135deg,#14b8a6,#0d9488)',
  'linear-gradient(135deg,#f97316,#ea580c)',
];

// ─── Sound helpers ────────────────────────────────────────────
const playBeep = (freq=440,dur=0.12,type:'sine'|'square'='sine',vol=0.3) => {
  try{
    const ctx=new AudioContext();
    const osc=ctx.createOscillator();
    const gain=ctx.createGain();
    osc.connect(gain);gain.connect(ctx.destination);
    osc.type=type;osc.frequency.value=freq;
    gain.gain.setValueAtTime(vol,ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001,ctx.currentTime+dur);
    osc.start();osc.stop(ctx.currentTime+dur);
  }catch{}
};
const playCorrect=()=>{
  playBeep(523,0.1,'sine');
  setTimeout(()=>playBeep(659,0.1,'sine'),110);
  setTimeout(()=>playBeep(784,0.18,'sine'),220);
};
const playWrong=()=>{
  playBeep(300,0.15,'square',0.25);
  setTimeout(()=>playBeep(250,0.2,'square',0.2),160);
};
const playClick=()=>playBeep(440,0.08,'sine',0.15);
const playReturn=()=>playBeep(330,0.08,'sine',0.12);

// ─── Types ───────────────────────────────────────────────────
interface WordItem {id:string;text:string;color:string;animState:'idle'|'bouncing'|'shaking';}
interface Exercise {
  id:number;
  words:WordItem[];    // ALL words (question order)
  answer:string;       // correct answer
  translation:string;  // dịch nghĩa tiếng Việt (tuỳ chọn)
  pool:string[];       // ids currently in pool
  arranged:string[];   // ids in answer area
  checked:boolean;
  isCorrect:boolean|null;
}

// ─── Parse input ─────────────────────────────────────────────
// Supported formats:
// Question line: [num.] word1 / word2 / word3           (words separated by /)
// Answer line:   [→ or > or ->] correct answer sentence  (immediately after question)
// Empty lines between exercises are OK.
const parseInput = (raw: string): Exercise[] => {
  // Split into non-empty lines
  const lines = raw.split('\n').map(l => l.trim()).filter(l => l && !l.startsWith('#'));
  const exs: Exercise[] = [];
  let i = 0;
  let exIdx = 0;

  const isQuestionLine = (line: string) => {
    // Strip leading number: "1." or "1)" or "1 ."
    const stripped = line.replace(/^\d+[\.)\s]+/, '').trim();
    // Must contain at least one "/" (word separator)
    return stripped.includes('/');
  };

  const parseQuestionLine = (line: string): string[] => {
    // Strip leading number ("1." or "2)" etc.)
    const stripped = line.replace(/^\d+[\.)\s]+/, '').trim();
    // Split by "/", trim each part, filter empty
    return stripped.split('/').map(w => w.trim()).filter(Boolean);
  };

  const parseAnswerLine = (line: string): string => {
    // Strip leading arrow variants: →, ->, >, =>
    return line.replace(/^[→>][-→>]?\s*/, '').replace(/^=>\s*/, '').trim();
  };

  const isAnswerLine = (line: string): boolean => {
    // It's an answer if it starts with arrow OR doesn't contain "/" and doesn't start with a number followed by "." or ")"
    if (/^[→>]/.test(line) || /^=>/.test(line)) return true;
    // Not a number-prefixed question and no slashes → plain answer sentence
    if (!line.includes('/') && !/^\d+[\.)\s]/.test(line)) return true;
    return false;
  };

  while (i < lines.length) {
    const line = lines[i];

    // Dòng câu hỏi luôn có dấu '/'
    if (!isQuestionLine(line)) { i++; continue; }

    const questionWords = parseQuestionLine(line);
    if (questionWords.length < 2) { i++; continue; }

    // Dòng tiếp theo: đáp án
    let answer = '';
    let translation = '';
    if (i + 1 < lines.length && isAnswerLine(lines[i + 1])) {
      answer = parseAnswerLine(lines[i + 1]);
      i += 2;
      // Dòng thứ 3 (tuỳ chọn): dịch tiếng Việt
      // Nhận ra vì KHÔNG chứa '/' (dòng câu hỏi mới luôn có '/')
      if (i < lines.length && !lines[i].includes('/')) {
        // Strip mũi tên đầu dòng (giống parseAnswerLine)
        translation = lines[i].replace(/^[→>][-→>]?\s*/, '').replace(/^=>\s*/, '').trim();
        i += 1;
      }
    } else {
      // Không tìm thấy dòng đáp án → bỏ qua
      i++;
      continue;
    }

    if (!answer) { continue; }

    // Build word items
    const words: WordItem[] = questionWords.map((w, wi) => ({
      id: `${exIdx}-${wi}`,
      text: w,
      color: WORD_COLORS[(exIdx + wi) % WORD_COLORS.length],
      animState: 'idle' as const,
    }));

    exs.push({
      id: exIdx++,
      words,
      answer,
      translation,
      pool: words.map(w => w.id),
      arranged: [],
      checked: false,
      isCorrect: null,
    });
  }

  return exs;
};

// ─── Main component ───────────────────────────────────────────
const SentenceOrderPage: React.FC = () => {
  const [rawInput,setRawInput] = useState('');
  const [exercises,setExercises] = useState<Exercise[]>([]);
  const [current,setCurrent] = useState(0);
  const [score,setScore] = useState(0);
  const [floats,setFloats] = useState<{id:number;x:number;emoji:string}[]>([]);
  const [showTeacherPanel,setShowTeacherPanel] = useState(true);
  const floatId = useRef(0);

  const ex = exercises[current] ?? null;
  const doneCount = exercises.filter(e=>e.checked).length;

  const spawnFloats = useCallback((emojis:string[])=>{
    const newFloats = emojis.map(emoji=>({id:floatId.current++,x:20+Math.random()*60,emoji}));
    setFloats(f=>[...f,...newFloats]);
    setTimeout(()=>setFloats(f=>f.filter(ff=>!newFloats.find(n=>n.id===ff.id))),1200);
  },[]);

  const triggerWordAnim = (exerciseIdx:number,wordId:string,state:'bouncing'|'shaking')=>{
    setExercises(prev=>prev.map((e,i)=>i!==exerciseIdx?e:{
      ...e,
      words:e.words.map(w=>w.id===wordId?{...w,animState:state}:w),
    }));
    setTimeout(()=>setExercises(prev=>prev.map((e,i)=>i!==exerciseIdx?e:{
      ...e,
      words:e.words.map(w=>w.id===wordId?{...w,animState:'idle'}:w),
    })),600);
  };

  // Click word in pool → move to answer area
  const handlePoolClick=(wordId:string)=>{
    if(!ex||ex.checked) return;
    playClick();
    triggerWordAnim(current,wordId,'bouncing');
    setExercises(prev=>prev.map((e,i)=>i!==current?e:{
      ...e,
      pool:e.pool.filter(id=>id!==wordId),
      arranged:[...e.arranged,wordId],
    }));
  };

  // Click word in answer area → return to pool (at the end)
  const handleAnswerClick=(wordId:string)=>{
    if(!ex||ex.checked) return;
    playReturn();
    triggerWordAnim(current,wordId,'bouncing');
    setExercises(prev=>prev.map((e,i)=>i!==current?e:{
      ...e,
      arranged:e.arranged.filter(id=>id!==wordId),
      pool:[...e.pool,wordId], // return to end of pool
    }));
  };

  const handleCheck=()=>{
    if(!ex||ex.checked||ex.arranged.length===0) return;
    const wordMap = Object.fromEntries(ex.words.map(w=>[w.id,w.text]));
    const arranged = ex.arranged.map(id=>wordMap[id]).join(' ');
    // Normalize: lowercase, collapse spaces, remove spaces before punctuation
    const normalize = (s: string) =>
      s.trim().toLowerCase()
        .replace(/\s+/g, ' ')             // collapse multiple spaces
        .replace(/\s+([.?!,;:])/g, '$1'); // "word ." → "word."
    const correct = normalize(arranged) === normalize(ex.answer);

    if(correct){
      playCorrect();
      spawnFloats(['⭐','🌟','✨','🎉','🥳','👏','⭐']);
      setScore(s=>s+1);
    } else {
      playWrong();
      // Shake all answer words
      ex.arranged.forEach(id=>triggerWordAnim(current,id,'shaking'));
    }
    setExercises(prev=>prev.map((e,i)=>i!==current?e:{...e,checked:true,isCorrect:correct}));
  };

  const handleReset=()=>{
    if(!ex) return;
    setExercises(prev=>prev.map((e,i)=>i!==current?e:{
      ...e,
      pool:e.words.map(w=>w.id),
      arranged:[],
      checked:false,
      isCorrect:null,
    }));
  };

  const handleLoad=()=>{
    const parsed=parseInput(rawInput);
    if(!parsed.length){
      alert('Không tìm thấy bài tập hợp lệ!\n\nĐịnh dạng:\n• Dòng câu hỏi: [STT.] từ1 / từ2 / từ3\n• Dòng đáp án: [→] Câu đúng\n\nVí dụ:\n1. is / my / It / mother / .\n→ It is my mother.\n\nBấm "Tải ví dụ" để xem mẫu.');
      return;
    }
    setExercises(parsed);
    setCurrent(0);
    setScore(0);
    setShowTeacherPanel(false); // auto-hide after loading to keep answers secret
  };

  const handleSampleLoad = () => {
    const sample =
`1. this / Who / is / ?
→ Who is this?
Đây là ai vậy?
2. that / Who / is / ?
→ Who is that?
Đó là ai vậy?
3. is / my / It / mother / .
→ It is my mother.
Đó là mẹ của tôi.
4. father / It / my / is / .
→ It is my father.
Đó là bố của tôi.
5. brother / is / my / This / .
→ This is my brother.
Đây là anh trai của tôi.`;
    setRawInput(sample);
  };

  const wordMap = ex ? Object.fromEntries(ex.words.map(w=>[w.id,w])) : {};

  return (
    <PageBg>
      <Stars />

      {/* Floating emojis on correct answer */}
      {floats.map(f=>(
        <FloatingEmoji key={f.id} x={f.x} animKey={f.id}>{f.emoji}</FloatingEmoji>
      ))}

      <Title>🔡 Sắp Xếp Câu</Title>

      {/* ─── Teacher Input Panel ─── */}
      <div style={{marginBottom:12}}>
        {/* Toggle bar — always visible */}
        <button
          onClick={() => setShowTeacherPanel(v => !v)}
          style={{
            width:'100%',
            display:'flex',alignItems:'center',justifyContent:'space-between',
            background: showTeacherPanel
              ? 'rgba(99,102,241,0.18)'
              : 'rgba(255,255,255,0.06)',
            border:'1px solid rgba(255,255,255,0.12)',
            borderRadius: showTeacherPanel ? '18px 18px 0 0' : '14px',
            padding:'10px 18px',
            cursor:'pointer',
            color:'#a5f3fc',
            fontSize:13,fontWeight:700,letterSpacing:1,
            transition:'all 0.2s',
          }}
        >
          <span>📋 Nhập bài tập (Giáo viên)</span>
          <span style={{fontSize:11,opacity:0.7,marginLeft:8}}>
            {showTeacherPanel ? '🔼 Thu gọn (ẩn đáp án)' : '🔽 Mở rộng'}
          </span>
        </button>

        {/* Collapsible body */}
        {showTeacherPanel && (
          <TeacherPanel style={{borderRadius:'0 0 18px 18px',borderTop:'none',marginBottom:0}}>
            <FormatHint>
              <strong style={{color:'#7dd3fc'}}>Định dạng mỗi bài tập (2–3 dòng liên tiếp):</strong><br/>
              • Dòng <span>câu hỏi</span>: <span>[STT.] từ1 / từ2 / từ3 / ...</span> — nhận diện bằng dấu <span style={{color:'#f59e0b'}}>/</span><br/>
              • Dòng <span>đáp án</span>: <span>[→] Câu đúng</span> (dấu → không bắt buộc)<br/>
              • Dòng <span style={{color:'#fde68a'}}>dịch tiếng Việt</span>: <span style={{color:'#94a3b8'}}>(tuỳ chọn)</span> — hiện cùng đáp án khi kiểm tra<br/>
              <span style={{color:'#475569'}}>Ví dụ: &nbsp;&nbsp;1. is / my / It / mother / .<br/>
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;→ It is my mother.<br/>
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Đó là mẹ của tôi. &nbsp;<span style={{color:'#6b7280'}}>(dòng dịch — tuỳ chọn)</span></span>
            </FormatHint>
            <Textarea
              placeholder={`1. this / Who / is / ?\n→ Who is this?\nĐây là ai vậy?\n2. that / Who / is / ?\n→ Who is that?\n3. is / my / It / mother / .\n→ It is my mother.\nĐó là mẹ của tôi.`}
              value={rawInput}
              onChange={e=>setRawInput(e.target.value)}
            />
            <BtnRow>
              <Btn variant="primary" onClick={handleLoad}>🚀 Tải bài tập ({parseInput(rawInput).length} câu)</Btn>
              <Btn variant="secondary" onClick={handleSampleLoad}>📄 Tải ví dụ</Btn>
              {exercises.length>0&&<Btn variant="secondary" onClick={()=>{setExercises([]);setCurrent(0);setScore(0);}}>🗑️ Xóa bài</Btn>}
            </BtnRow>
          </TeacherPanel>
        )}
      </div>

      {/* ─── Student Board ─── */}
      {exercises.length===0 ? <EmptyState/> : (
        <QuestionBoard>
          {/* Header */}
          <QHeader>
            <QBadge>Câu {current+1} / {exercises.length}</QBadge>
            <ProgressBar pct={((doneCount)/exercises.length)*100}/>
            <ScoreBadge>⭐ {score} điểm</ScoreBadge>
          </QHeader>

          {/* Result banner */}
          {ex.checked && ex.isCorrect!==null && (
            <ResultBanner ok={ex.isCorrect}>
              {ex.isCorrect ? '🎉 Tuyệt vời! Đúng rồi! 🎉' : '😅 Chưa đúng rồi! Thử lại nhé!'}
            </ResultBanner>
          )}

          {/* Correct answer hint (after check or wrong) */}
          {ex.checked && (
            <CorrectAnswerHint>
              💡 Đáp án đúng: <span>"{ex.answer}"</span>
              {ex.translation && (
                <div style={{marginTop:'10px',paddingTop:'10px',borderTop:'1px solid rgba(99,102,241,0.2)',display:'flex',alignItems:'center',gap:'8px',fontSize:'40px'}}>
                  🇻🇳 <span style={{color:'#fde68a',fontWeight:700}}>{ex.translation}</span>
                </div>
              )}
            </CorrectAnswerHint>
          )}

          {/* Word pool (question order) */}
          <SectionLabel>
            📦 Ngân hàng từ {!ex.checked&&<span style={{color:'#475569',textTransform:'none',fontWeight:400}}>(nhấn vào từ để sắp xếp)</span>}
          </SectionLabel>
          <WordPool>
            {ex.pool.length===0 && (
              <div style={{color:'#334155',fontSize:13,fontStyle:'italic',padding:'4px 8px'}}>
                Đã chọn hết từ 👍
              </div>
            )}
            {ex.pool.map(id=>{
              const w=wordMap[id];
              if(!w) return null;
              return (
                <WordBox
                  key={id}
                  color={w.color}
                  state="pool"
                  animState={w.animState}
                  onClick={()=>handlePoolClick(id)}
                  disabled={ex.checked}
                  title="Nhấn để thêm vào câu trả lời"
                >
                  {w.text}
                </WordBox>
              );
            })}
          </WordPool>

          {/* Answer area */}
          <SectionLabel>
            ✏️ Câu trả lời của em
            {ex.arranged.length>0&&!ex.checked&&<span style={{color:'#475569',textTransform:'none',fontWeight:400}}> (nhấn vào từ để trả lại)</span>}
          </SectionLabel>
          <AnswerArea isCorrect={ex.isCorrect===true} isWrong={ex.isCorrect===false}>
            {ex.arranged.length===0&&(
              <AnswerPlaceholder>Nhấn vào các từ bên trên để sắp xếp câu...</AnswerPlaceholder>
            )}
            {ex.arranged.map(id=>{
              const w=wordMap[id];
              if(!w) return null;
              return (
                <WordBox
                  key={id}
                  color={w.color}
                  state="answer"
                  animState={w.animState}
                  onClick={()=>handleAnswerClick(id)}
                  disabled={ex.checked}
                  title="Nhấn để trả lại"
                  style={{opacity:ex.checked?0.85:1}}
                >
                  {w.text}
                </WordBox>
              );
            })}
          </AnswerArea>

          {/* Action buttons */}
          <NavRow style={{marginBottom:16}}>
            {!ex.checked ? (
              <>
                <Btn variant="success"
                  onClick={handleCheck}
                  disabled={ex.arranged.length===0}
                  pulsing={ex.arranged.length===ex.words.length&&!ex.checked}
                  style={{fontSize:16,padding:'11px 28px'}}
                >
                  ✅ Kiểm tra
                </Btn>
                <Btn variant="warning" onClick={handleReset}>🔄 Làm lại</Btn>
              </>
            ) : (
              <Btn variant="warning" onClick={handleReset} style={{fontSize:14}}>🔄 Thử lại câu này</Btn>
            )}
          </NavRow>

          {/* Navigation */}
          <NavRow>
            <Btn variant="secondary"
              onClick={()=>{setCurrent(c=>Math.max(0,c-1));}}
              disabled={current===0}
            >◀ Câu trước</Btn>

            {/* Question dots */}
            <div style={{display:'flex',gap:6,alignItems:'center',flexWrap:'wrap',justifyContent:'center'}}>
              {exercises.map((_,i)=>(
                <button
                  key={i}
                  onClick={()=>setCurrent(i)}
                  style={{
                    width:32,height:32,borderRadius:'50%',border:'none',cursor:'pointer',
                    fontSize:13,fontWeight:700,
                    background:i===current
                      ?'linear-gradient(135deg,#6366f1,#ec4899)'
                      :exercises[i].checked
                        ?exercises[i].isCorrect?'#10b981':'#ef4444'
                        :'rgba(255,255,255,0.1)',
                    color:'#fff',
                    boxShadow:i===current?'0 0 0 3px rgba(99,102,241,0.5)':'none',
                    transition:'all 0.2s',
                  }}
                >{i+1}</button>
              ))}
            </div>

            <Btn variant="secondary"
              onClick={()=>{setCurrent(c=>Math.min(exercises.length-1,c+1));}}
              disabled={current===exercises.length-1}
            >Câu tiếp ▶</Btn>
          </NavRow>

          {/* Overall summary */}
          {doneCount===exercises.length&&exercises.length>0&&(
            <div style={{marginTop:20,padding:20,borderRadius:16,
              background:'linear-gradient(135deg,rgba(16,185,129,0.15),rgba(99,102,241,0.15))',
              border:'1px solid rgba(16,185,129,0.3)',textAlign:'center'}}>
              <div style={{fontSize:36,marginBottom:8}}>
                {score===exercises.length?'🏆':score>=exercises.length/2?'🌟':'😊'}
              </div>
              <div style={{color:'#6ee7b7',fontWeight:900,fontSize:20}}>
                Hoàn thành! {score}/{exercises.length} câu đúng
              </div>
              <div style={{color:'#94a3b8',fontSize:14,marginTop:4}}>
                {score===exercises.length?'Xuất sắc! Em giỏi lắm! 🎉':
                 score>=exercises.length*0.7?'Rất tốt! Cố gắng thêm nhé! 💪':
                 'Cần cố gắng hơn! Thầy cô tin em làm được! 🤗'}
              </div>
              <Btn variant="primary" style={{margin:'12px auto 0',display:'inline-flex'}}
                onClick={()=>{
                  setExercises(parsed=>parsed.map(e=>({
                    ...e,
                    pool:e.words.map(w=>w.id),arranged:[],checked:false,isCorrect:null
                  })));
                  setCurrent(0);setScore(0);
                }}>
                🔄 Làm lại từ đầu
              </Btn>
            </div>
          )}
        </QuestionBoard>
      )}
    </PageBg>
  );
};

export default SentenceOrderPage;
